import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { MFASetup } from './MFASetup';
import { ChangePassword } from './ChangePassword';

interface MFASettingsProps {
  token: string;
  onMFAEnabled?: () => void;
}

export const MFASettings: React.FC<MFASettingsProps> = ({ token, onMFAEnabled }) => {
  const [loading, setLoading] = useState(true);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaType, setMfaType] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Setup dialog
  const [showSetup, setShowSetup] = useState(false);
  
  // Disable dialog
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disabling, setDisabling] = useState(false);

  useEffect(() => {
    fetchMFAStatus();
  }, []);

  const fetchMFAStatus = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/auth/mfa/status`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch MFA status');
      }
      
      const data = await response.json();
      setMfaEnabled(data.enabled);
      setMfaType(data.type);
    } catch (err: any) {
      setError(err.message || 'Failed to load MFA status');
    } finally {
      setLoading(false);
    }
  };

  const handleDisableMFA = async () => {
    setDisabling(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/auth/mfa/disable`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ password: disablePassword })
        }
      );
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to disable MFA');
      }
      
      const data = await response.json();
      setSuccess(data.message);
      setShowDisableDialog(false);
      setDisablePassword('');
      await fetchMFAStatus();
    } catch (err: any) {
      setError(err.message || 'Failed to disable MFA');
    } finally {
      setDisabling(false);
    }
  };

  const handleSetupComplete = () => {
    setShowSetup(false);
    setSuccess('MFA has been enabled successfully!');
    fetchMFAStatus();
    if (onMFAEnabled) {
      onMFAEnabled();
    }
  };

  const getMFATypeLabel = () => {
    if (mfaType === 'totp') return 'Authenticator App';
    if (mfaType === 'email') return 'Email OTP';
    return 'Unknown';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (showSetup) {
    return <MFASetup token={token} onComplete={handleSetupComplete} />;
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
        Security Settings
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Change Password Section */}
      <ChangePassword token={token} />

      {/* MFA Section */}
      <Paper sx={{ p: 3, mb: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <SecurityIcon sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Multi-Factor Authentication
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add an extra layer of security to your account
            </Typography>
          </Box>
          {mfaEnabled && (
            <Chip
              icon={<CheckCircleIcon />}
              label="Enabled"
              color="success"
              sx={{ ml: 2 }}
            />
          )}
        </Box>

        {mfaEnabled ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              MFA is currently enabled using <strong>{getMFATypeLabel()}</strong>
            </Alert>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Your account is protected with multi-factor authentication. You'll need to verify your identity with a code each time you log in.
            </Typography>

            <Button
              variant="outlined"
              color="error"
              onClick={() => setShowDisableDialog(true)}
            >
              Disable MFA
            </Button>
          </Box>
        ) : (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              MFA is not enabled. Your account is less secure without it.
            </Alert>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enable multi-factor authentication to add an extra layer of security to your account. 
              You can choose between email-based codes or an authenticator app.
            </Typography>

            <Button
              variant="contained"
              onClick={() => setShowSetup(true)}
            >
              Enable MFA
            </Button>
          </Box>
        )}
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          About Multi-Factor Authentication
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Multi-factor authentication (MFA) adds an extra layer of security to your account by requiring 
          a verification code in addition to your password when logging in.
        </Typography>

        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Available Methods:
        </Typography>
        
        <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
          <li>
            <Typography variant="body2">
              <strong>Email OTP:</strong> Receive a 6-digit code via email each time you log in
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <strong>Authenticator App:</strong> Use Google Authenticator, Authy, or similar apps to generate codes
            </Typography>
          </li>
        </Box>

        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Important: Backup Codes
          </Typography>
          <Typography variant="body2">
            When you enable MFA, you'll receive backup codes. Save these in a secure location. 
            They can be used to access your account if you lose access to your primary MFA method.
          </Typography>
        </Alert>
      </Paper>

      {/* Disable MFA Dialog */}
      <Dialog open={showDisableDialog} onClose={() => setShowDisableDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Disable Multi-Factor Authentication</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Disabling MFA will make your account less secure. Are you sure you want to continue?
          </Alert>
          
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter your password to confirm:
          </Typography>
          
          <TextField
            fullWidth
            type="password"
            label="Password"
            value={disablePassword}
            onChange={(e) => setDisablePassword(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowDisableDialog(false);
            setDisablePassword('');
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDisableMFA}
            disabled={disabling || !disablePassword}
          >
            {disabling ? <CircularProgress size={24} /> : 'Disable MFA'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
